import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, ChevronRight, DollarSign, TrendingUp, Eye, MousePointerClick, Users,
  Target, Megaphone, Layers, AlertTriangle, Zap, BarChart3, SlidersHorizontal, Activity, Trash2,
} from 'lucide-react';
import { Card, Btn, StatCard, EmptyState, Modal } from '../components/UI';
import { fetchMetaInsights, summarize } from '../utils/meta';
import { META_CLIENTS } from '../utils/metaClients';

const sel = { padding: '9px 12px', fontSize: 13, background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', color: 'var(--text)', fontFamily: 'var(--font-ui)', cursor: 'pointer' };
const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7d', label: 'Last 7 days' },
  { id: 'last_14d', label: 'Last 14 days' },
  { id: 'last_30d', label: 'Last 30 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'custom', label: 'Custom range' },
];

// KPI metrics the user can toggle at the top + save as named views (per-browser).
const ALL_METRICS = [
  { key: 'spend', label: 'Amount Spent' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'roas', label: 'ROAS' },
  { key: 'results', label: 'Results (auto)' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'view_content', label: 'View Content' },
  { key: 'add_to_cart', label: 'Add to Cart' },
  { key: 'initiate_checkout', label: 'Initiate Checkout' },
  { key: 'add_payment_info', label: 'Add Payment Info' },
  { key: 'reach', label: 'Reach' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks + CTR' },
  { key: 'cpm', label: 'CPM' },
  { key: 'frequency', label: 'Frequency' },
];
const DEFAULT_METRICS = ['spend', 'revenue', 'roas', 'results', 'add_to_cart', 'initiate_checkout', 'add_payment_info', 'reach', 'impressions', 'clicks'];
const PREFS_KEY = 'strategists_ad_metric_prefs_v1';
function loadPrefs() {
  try { const s = JSON.parse(localStorage.getItem(PREFS_KEY)); if (s && Array.isArray(s.metrics)) return { metrics: s.metrics, views: s.views || [] }; } catch (e) { /* */ }
  return { metrics: DEFAULT_METRICS, views: [] };
}
function savePrefs(p) { try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch (e) { /* */ } }

const STATUS = {
  ACTIVE:            { label: 'Active',      bg: 'var(--green-bg)',  color: 'var(--green)' },
  PAUSED:            { label: 'Paused',      bg: 'var(--bg4)',       color: 'var(--text3)' },
  CAMPAIGN_PAUSED:   { label: 'Paused',      bg: 'var(--bg4)',       color: 'var(--text3)' },
  ADSET_PAUSED:      { label: 'Paused',      bg: 'var(--bg4)',       color: 'var(--text3)' },
  IN_PROCESS:        { label: 'In review',   bg: 'var(--orange-bg)', color: 'var(--orange)' },
  PENDING_REVIEW:    { label: 'In review',   bg: 'var(--orange-bg)', color: 'var(--orange)' },
  WITH_ISSUES:       { label: 'Issues',      bg: 'var(--red-bg)',    color: 'var(--red)' },
  DISAPPROVED:       { label: 'Disapproved', bg: 'var(--red-bg)',    color: 'var(--red)' },
  ARCHIVED:          { label: 'Archived',    bg: 'var(--bg4)',       color: 'var(--text3)' },
  DELETED:           { label: 'Deleted',     bg: 'var(--bg4)',       color: 'var(--text3)' },
};
function StatusPill({ status }) {
  const s = STATUS[status] || { label: (status || '—').replace(/_/g, ' ').toLowerCase(), bg: 'var(--bg4)', color: 'var(--text3)' };
  return <span style={{ background: s.bg, color: s.color, fontSize: 10.5, fontFamily: 'var(--mono)', fontWeight: 400, padding: '3px 8px', borderRadius: 'var(--radius)', textTransform: 'capitalize', letterSpacing: '0.02em', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    {status === 'ACTIVE' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />}{s.label}
  </span>;
}

const money = (n, cur) => `${cur || ''} ${Math.round(n || 0).toLocaleString()}`.trim();
const compact = (n) => { n = n || 0; if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'; if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K'; return String(Math.round(n)); };

// Clean, adaptive metric row: Spend · <the campaign's actual result> · cost/result · reach · CTR.
// The result (Messages / Purchases / Leads / Profile visits / Link clicks / Reach…) and its
// label come from the backend, resolved per campaign's objective — so every goal type shows.
function MetricStrip({ node, cur }) {
  const m = node && node.metrics; const r = node && node.result;
  if (!m) return <div style={{ fontSize: 12, color: 'var(--text3)' }}>No delivery in this range</div>;
  const cell = (label, val, strong) => (
    <div style={{ minWidth: 58 }}>
      <div style={{ fontSize: 9.5, fontFamily: 'var(--mono)', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: strong ? 700 : 600, color: strong || 'var(--text)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{val}</div>
    </div>
  );
  const awareness = r && (r.key === 'reach' || r.key === 'impr');
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
      {cell('Spend', money(m.spend, cur))}
      {r && cell(r.label, compact(r.count), 'var(--accent2)')}
      {r && !awareness && cell(r.costLabel, money(r.cost, cur))}
      {r && r.roas ? cell('ROAS', `${r.roas.toFixed(2)}x`, 'var(--green)') : null}
      {cell('Reach', compact(m.reach))}
      {cell('CTR', `${(m.ctr || 0).toFixed(2)}%`)}
    </div>
  );
}

export default function AdsPage() {
  const [clientIdx, setClientIdx] = useState(0);
  const [preset, setPreset] = useState('last_30d');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});   // {entityId: true}
  const [liveOnly, setLiveOnly] = useState(false);
  const [metrics, setMetrics] = useState(() => loadPrefs().metrics);
  const [savedViews, setSavedViews] = useState(() => loadPrefs().views);
  const [metricMenu, setMetricMenu] = useState(false);
  const [viewName, setViewName] = useState('');
  useEffect(() => { savePrefs({ metrics, views: savedViews }); }, [metrics, savedViews]);

  const client = META_CLIENTS[clientIdx];
  const cur = data?.account?.currency || client?.currency || '';

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true); setError(''); setData(null);
    try {
      const opts = { adAccountId: client.adAccountId };
      if (preset === 'custom') { if (!since || !until) { setLoading(false); return; } opts.since = since; opts.until = until; }
      else opts.datePreset = preset;
      const res = await fetchMetaInsights(opts);
      setData(res);
    } catch (e) { setError(e.message || 'Failed to load Meta data.'); }
    setLoading(false);
  }, [client, preset, since, until]);

  useEffect(() => { if (preset !== 'custom') load(); /* eslint-disable-next-line */ }, [clientIdx, preset]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const kpi = data ? (data.accountMetrics || summarize(data.campaigns)) : null;
  // Group campaigns by their resolved result type → adaptive KPI cards (Messages, Leads,
  // Profile visits, …), each with an accurate cost/result from its own campaigns' spend.
  const resultGroups = data ? (() => {
    const g = {};
    data.campaigns.forEach(c => {
      const r = c.result; if (!r || !r.count || r.key === 'reach' || r.key === 'impr' || r.key === 'purchase') return;
      const e = g[r.label] || (g[r.label] = { label: r.label, count: 0, spend: 0 });
      e.count += r.count; e.spend += (c.metrics?.spend || 0);
    });
    return Object.values(g).sort((a, b) => b.spend - a.spend).slice(0, 3);
  })() : [];
  const visibleCampaigns = data ? (liveOnly ? data.campaigns.filter(c => c.status === 'ACTIVE') : data.campaigns) : [];
  const campaignsBySpend = [...visibleCampaigns].filter(c => c.metrics).sort((a, b) => b.metrics.spend - a.metrics.spend);
  const maxSpend = campaignsBySpend.reduce((m, c) => Math.max(m, c.metrics.spend), 0) || 1;

  const ICON = { spend: DollarSign, revenue: TrendingUp, roas: TrendingUp, purchases: Target, reach: Users, impressions: Eye, clicks: MousePointerClick, cpm: BarChart3, frequency: Activity, view_content: Eye, add_to_cart: Target, initiate_checkout: MousePointerClick, add_payment_info: BarChart3 };
  function metricCard(key) {
    if (!kpi) return null;
    const card = (label, value, sub, icon, col, bg) => <StatCard key={key} label={label} value={value} sub={sub} icon={icon} iconColor={col} iconBg={bg} />;
    switch (key) {
      case 'spend': return card('Amount Spent', money(kpi.spend, cur), '', ICON.spend, 'var(--accent)', 'rgba(169,137,83,0.12)');
      case 'revenue': return kpi.value > 0 ? card('Revenue (Meta)', money(kpi.value, cur), `${(kpi.roas || 0).toFixed(2)}x ROAS`, ICON.revenue, 'var(--green)', 'var(--green-bg)') : null;
      case 'roas': return kpi.value > 0 ? card('ROAS', `${(kpi.roas || 0).toFixed(2)}x`, '', ICON.roas, 'var(--green)', 'var(--green-bg)') : null;
      case 'results': return resultGroups.map(g => <StatCard key={'r-' + g.label} label={g.label} value={compact(g.count)} sub={g.count ? `${money(g.spend / g.count, cur)} / result` : ''} icon={Target} iconColor="var(--purple)" iconBg="var(--purple-bg)" />);
      case 'purchases': return card('Purchases', compact(kpi.purchases), '', ICON.purchases, 'var(--purple)', 'var(--purple-bg)');
      case 'view_content': return card('View Content', compact(kpi.viewContent || 0), kpi.viewContent ? `${money(kpi.spend / kpi.viewContent, cur)} / view` : '', ICON.view_content, 'var(--blue)', 'var(--blue-bg)');
      case 'add_to_cart': return card('Add to Cart', compact(kpi.addToCart || 0), kpi.addToCart ? `${money(kpi.spend / kpi.addToCart, cur)} / cart` : '', ICON.add_to_cart, 'var(--purple)', 'var(--purple-bg)');
      case 'initiate_checkout': return card('Initiate Checkout', compact(kpi.initiateCheckout || 0), kpi.initiateCheckout ? `${money(kpi.spend / kpi.initiateCheckout, cur)} / checkout` : '', ICON.initiate_checkout, 'var(--orange)', 'var(--orange-bg)');
      case 'add_payment_info': return card('Add Payment Info', compact(kpi.addPaymentInfo || 0), kpi.addPaymentInfo ? `${money(kpi.spend / kpi.addPaymentInfo, cur)} / add` : '', ICON.add_payment_info, 'var(--green)', 'var(--green-bg)');
      case 'reach': return card('Reach', compact(kpi.reach), '', ICON.reach, 'var(--accent2)', 'rgba(169,137,83,0.12)');
      case 'impressions': return card('Impressions', compact(kpi.impressions), '', ICON.impressions, 'var(--blue)', 'var(--blue-bg)');
      case 'clicks': return card('Clicks', compact(kpi.clicks), `${(kpi.ctr || 0).toFixed(2)}% CTR`, ICON.clicks, 'var(--orange)', 'var(--orange-bg)');
      case 'cpm': return card('CPM', money(kpi.cpm, cur), '', ICON.cpm, 'var(--text2)', 'var(--bg4)');
      case 'frequency': return card('Frequency', (kpi.frequency || (kpi.reach ? kpi.impressions / kpi.reach : 0)).toFixed(2), '', ICON.frequency, 'var(--text2)', 'var(--bg4)');
      default: return null;
    }
  }

  return (
    <div>
      {/* Header + controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontFamily: 'var(--serif)', fontWeight: 400, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 34, height: 34, borderRadius: 'var(--radius)', background: 'var(--primary)', color: 'var(--bg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Zap size={18} /></span>
            Ads
          </h2>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: 'var(--text3)' }}>Live campaign performance — straight from Meta. Read-only.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={sel} value={clientIdx} onChange={e => setClientIdx(Number(e.target.value))}>
            {META_CLIENTS.map((c, i) => <option key={c.adAccountId} value={i}>{c.name}</option>)}
          </select>
          <select style={sel} value={preset} onChange={e => setPreset(e.target.value)}>
            {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          {preset === 'custom' && <>
            <input type="date" style={sel} value={since} onChange={e => setSince(e.target.value)} />
            <input type="date" style={sel} value={until} onChange={e => setUntil(e.target.value)} />
            <Btn variant="primary" onClick={load} disabled={!since || !until}>Go</Btn>
          </>}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: liveOnly ? 'var(--accent2)' : 'var(--text2)', cursor: 'pointer', padding: '0 4px', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={liveOnly} onChange={e => setLiveOnly(e.target.checked)} />Live only
          </label>
          <Btn variant="ghost" onClick={() => setMetricMenu(true)} title="Choose which metrics show up top"><SlidersHorizontal size={15} />Metrics</Btn>
          <Btn variant="ghost" onClick={load} disabled={loading} title="Refresh"><RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /></Btn>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12 }}>
          {Array.from({ length: 6 }).map((_, i) => <Card key={i} style={{ height: 96, background: 'var(--bg3)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {!loading && error && (
        <Card style={{ padding: 20, borderLeft: '3px solid var(--red)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <AlertTriangle size={20} color="var(--red)" />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>Couldn't load Meta data</div>
            <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 2 }}>{error}</div>
          </div>
          <Btn variant="primary" onClick={load}>Retry</Btn>
        </Card>
      )}

      {!loading && !error && data && (
        <>
          {/* KPI cards — driven by the chosen metrics view */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(168px,1fr))', gap: 12, marginBottom: 18 }}>
            {metrics.map(k => metricCard(k))}
          </div>

          {/* Spend by campaign */}
          {campaignsBySpend.length > 0 && (
            <Card style={{ padding: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7 }}><BarChart3 size={15} color="var(--accent2)" />Spend by campaign</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {campaignsBySpend.slice(0, 8).map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: '32%', minWidth: 120, fontSize: 12.5, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>{c.name}</div>
                    <div style={{ flex: 1, height: 22, background: 'var(--bg3)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(3, (c.metrics.spend / maxSpend) * 100)}%`, background: 'linear-gradient(90deg,var(--accent2),var(--accent))', borderRadius: 'var(--radius)', transition: 'width .4s' }} />
                    </div>
                    <div style={{ width: 96, textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{money(c.metrics.spend, cur)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Campaign → adset → ad tree */}
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: '4px 2px 10px', display: 'flex', alignItems: 'center', gap: 7 }}><Megaphone size={15} color="var(--accent2)" />{liveOnly ? 'Live campaigns' : 'Campaigns'} <span style={{ color: 'var(--text3)', fontWeight: 500 }}>({visibleCampaigns.length})</span></div>
          {visibleCampaigns.length === 0
            ? <Card><EmptyState message={liveOnly ? 'No active campaigns in this range.' : 'No campaigns in this account for the selected range.'} /></Card>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visibleCampaigns.map(c => (
                <Card key={c.id} style={{ padding: 0, overflow: 'hidden' }}>
                  <div onClick={() => toggle(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 15px', cursor: 'pointer', flexWrap: 'wrap' }}>
                    <ChevronRight size={16} color="var(--text3)" style={{ transform: expanded[c.id] ? 'rotate(90deg)' : 'none', transition: 'transform .18s', flexShrink: 0 }} />
                    <div style={{ minWidth: 160, flex: '1 1 220px' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
                      <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginTop: 4 }}>
                        <StatusPill status={c.status} />
                        {c.objective && <span style={{ fontSize: 10.5, color: 'var(--text3)', textTransform: 'capitalize' }}>{String(c.objective).replace(/OUTCOME_|_/g, ' ').toLowerCase().trim()}</span>}
                      </div>
                    </div>
                    <MetricStrip node={c} cur={cur} />
                  </div>

                  {expanded[c.id] && (
                    <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg3)', padding: '6px 10px 10px' }}>
                      {(c.adsets || []).length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 10px' }}>No ad sets.</div>}
                      {(c.adsets || []).map(s => (
                        <div key={s.id} style={{ marginTop: 6, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                          <div onClick={() => toggle(s.id)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', cursor: 'pointer', flexWrap: 'wrap' }}>
                            <ChevronRight size={14} color="var(--text3)" style={{ transform: expanded[s.id] ? 'rotate(90deg)' : 'none', transition: 'transform .18s', flexShrink: 0 }} />
                            <Layers size={13} color="var(--accent2)" />
                            <div style={{ flex: '1 1 200px', minWidth: 140 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{s.name}</span>
                              <span style={{ marginLeft: 8 }}><StatusPill status={s.status} /></span>
                            </div>
                            <MetricStrip node={s} cur={cur} />
                          </div>
                          {expanded[s.id] && (
                            <div style={{ borderTop: '1px solid var(--border)', padding: '4px 8px 8px' }}>
                              {(s.ads || []).length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px' }}>No ads.</div>}
                              {(s.ads || []).map(a => (
                                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                                  <Target size={12} color="var(--text3)" style={{ marginLeft: 18 }} />
                                  <div style={{ flex: '1 1 180px', minWidth: 130 }}>
                                    <span style={{ fontSize: 12.5, color: 'var(--text)' }}>{a.name}</span>
                                    <span style={{ marginLeft: 8 }}><StatusPill status={a.status} /></span>
                                  </div>
                                  <MetricStrip node={a} cur={cur} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>}

          <div style={{ marginTop: 16, fontSize: 11.5, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Eye size={12} /> Read-only view · {data.account?.name} · {cur} · pulled live from Meta
          </div>
        </>
      )}

      <Modal open={metricMenu} onClose={() => setMetricMenu(false)} title="Customize metrics" width={360}>
        <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text3)', marginBottom: 8 }}>Show at top</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
          {ALL_METRICS.map(m => {
            const on = metrics.includes(m.key);
            return <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 3px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}>
              <input type="checkbox" checked={on} onChange={() => setMetrics(prev => on ? prev.filter(k => k !== m.key) : [...prev, m.key])} />{m.label}
            </label>;
          })}
        </div>
        <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0 0', paddingTop: 12 }}>
          {savedViews.length > 0 && <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text3)', marginBottom: 6 }}>Saved views</div>}
          {savedViews.map(v => (
            <div key={v.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <button onClick={() => { setMetrics([...v.metrics]); setMetricMenu(false); }} style={{ flex: 1, textAlign: 'left', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}>{v.name}</button>
              <button onClick={() => setSavedViews(sv => sv.filter(x => x.name !== v.name))} title="Delete view" style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', padding: 4 }}><Trash2 size={15} /></button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input value={viewName} onChange={e => setViewName(e.target.value)} placeholder="Save current as a view…" style={{ ...sel, flex: 1, padding: '8px 10px', fontSize: 13, background: 'var(--bg3)' }} />
            <Btn variant="primary" disabled={!viewName.trim()} onClick={() => { const n = viewName.trim(); setSavedViews(sv => [...sv.filter(x => x.name !== n), { name: n, metrics: [...metrics] }]); setViewName(''); }}>Save</Btn>
          </div>
          <button onClick={() => setMetrics([...DEFAULT_METRICS])} style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12.5, cursor: 'pointer', fontWeight: 600 }}>Reset to default</button>
        </div>
      </Modal>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
